import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../contexts/AuthContext";
import DashboardNav from "../components/DashboardNav";
import {
  ArrowLeft,
  Shield,
  CheckCircle2,
  XCircle,
  Pause,
  Play,
  Users,
  DollarSign,
  CalendarDays,
  Gamepad2,
  Eye,
  Hash,
  Link as LinkIcon,
  Globe,
  MonitorPlay,
  FolderOpen,
  ExternalLink,
  AlertCircle,
  Loader2,
  Pencil,
} from "lucide-react";

function AdminCampaignReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [campaign, setCampaign] = useState(null);
  const [publisher, setPublisher] = useState(null);
  const [joinedInfluencers, setJoinedInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, "campaigns", id));
        if (!snap.exists()) {
          setError("Campaign not found.");
          setLoading(false);
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        setCampaign(data);

        // Fetch publisher info
        if (data.publisherId) {
          const pubSnap = await getDoc(doc(db, "users", data.publisherId));
          if (pubSnap.exists()) setPublisher(pubSnap.data());
        }

        // Fetch joined influencers
        const joinsSnap = await getDocs(
          query(collection(db, "campaignJoins"), where("campaignId", "==", id))
        );
        const joins = joinsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Fetch influencer profiles
        const enriched = await Promise.all(
          joins.map(async (join) => {
            try {
              const userSnap = await getDoc(doc(db, "users", join.influencerId));
              return {
                ...join,
                profile: userSnap.exists() ? userSnap.data() : null,
              };
            } catch {
              return { ...join, profile: null };
            }
          })
        );
        setJoinedInfluencers(enriched);
      } catch (err) {
        console.error(err);
        setError("Failed to load campaign.");
      }
      setLoading(false);
    }
    load();
  }, [id]);

  async function handleStatusChange(newStatus) {
    setActionLoading(newStatus);
    setError("");
    setSuccess("");
    try {
      await updateDoc(doc(db, "campaigns", id), { status: newStatus });
      setCampaign((prev) => ({ ...prev, status: newStatus }));
      setSuccess(
        newStatus === "active"
          ? "Campaign approved and set to active!"
          : newStatus === "paused"
          ? "Campaign has been paused."
          : `Campaign status changed to ${newStatus}.`
      );
      try {
        const sendCampaignStatusEmail = httpsCallable(functions, "sendCampaignStatusEmail");
        await sendCampaignStatusEmail({ campaignId: id, newStatus });
      } catch (emailErr) {
        console.error("Failed to send campaign status email:", emailErr);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to update campaign status.");
    }
    setActionLoading("");
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="max-w-4xl mx-auto px-4 py-24 text-center">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (error && !campaign) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="max-w-4xl mx-auto px-4 py-24 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  const statusColors = {
    pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    active: "bg-green-500/10 text-green-400 border-green-500/20",
    paused: "bg-gray-500/10 text-gray-400 border-gray-500/20",
    rejected: "bg-red-500/10 text-red-400 border-red-500/20",
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate("/admin")}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-300 transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Admin Panel
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-4">
            {campaign.coverImageUrl && (
              <img
                src={campaign.coverImageUrl}
                alt={campaign.title}
                className="w-20 h-20 rounded-2xl object-cover border border-gray-800"
              />
            )}
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl font-bold">{campaign.title}</h1>
                <span
                  className={`text-xs px-2.5 py-1 rounded-full font-medium border ${
                    statusColors[campaign.status] || statusColors.pending
                  }`}
                >
                  {campaign.status}
                </span>
              </div>
              <p className="text-gray-400 text-sm">{campaign.gameName}</p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate(`/campaigns/${id}/edit`)}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-xl transition-all font-semibold text-sm"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={() => navigate(`/campaigns/${id}`)}
              className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-xl transition-all font-semibold text-sm"
            >
              <Eye className="w-4 h-4" />
              Upload CSV / Insights
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5 mb-6">
          <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-brand-400" />
            Admin Actions
          </h3>
          <div className="flex flex-wrap gap-3">
            {campaign.status !== "active" && (
              <button
                onClick={() => handleStatusChange("active")}
                disabled={!!actionLoading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl transition-all font-semibold text-sm"
              >
                {actionLoading === "active" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Approve & Activate
              </button>
            )}
            {campaign.status !== "paused" && campaign.status !== "pending" && (
              <button
                onClick={() => handleStatusChange("paused")}
                disabled={!!actionLoading}
                className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl transition-all font-semibold text-sm"
              >
                {actionLoading === "paused" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Pause className="w-4 h-4" />
                )}
                Pause Campaign
              </button>
            )}
            {campaign.status === "pending" && (
              <button
                onClick={() => handleStatusChange("rejected")}
                disabled={!!actionLoading}
                className="flex items-center gap-2 bg-red-600/80 hover:bg-red-600 disabled:opacity-50 text-white px-5 py-2.5 rounded-xl transition-all font-semibold text-sm"
              >
                {actionLoading === "rejected" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                Reject
              </button>
            )}
          </div>

          {success && (
            <div className="mt-3 bg-green-500/10 border border-green-500/20 text-green-400 text-sm rounded-xl px-4 py-2.5">
              {success}
            </div>
          )}
          {error && campaign && (
            <div className="mt-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl px-4 py-2.5">
              {error}
            </div>
          )}
        </div>

        {/* Publisher Info */}
        {publisher && (
          <div
            onClick={() => navigate(`/admin/publishers/${campaign.publisherId}`)}
            className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5 mb-6 hover:border-gray-700 transition-colors cursor-pointer"
          >
            <h3 className="text-sm font-medium text-gray-300 mb-3">Publisher</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 font-bold text-sm">
                  {(publisher.displayName || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold">{publisher.displayName || "Unnamed"}</p>
                  <p className="text-sm text-gray-500">{publisher.email}</p>
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/publishers/${campaign.publisherId}`);
                }}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-gray-300 px-4 py-2 rounded-xl transition-all text-sm font-medium"
              >
                <Eye className="w-4 h-4" />
                View
              </button>
            </div>
          </div>
        )}

        {/* Campaign Details */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5 mb-6">
          <h3 className="text-sm font-medium text-gray-300 mb-4">Campaign Details</h3>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div className="flex items-center gap-2 text-sm">
              <Gamepad2 className="w-4 h-4 text-gray-500" />
              <span className="text-gray-400">Type:</span>
              <span className="capitalize font-medium">{campaign.campaignType}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Hash className="w-4 h-4 text-gray-500" />
              <span className="text-gray-400">Steam App ID:</span>
              <span className="font-medium">{campaign.steamAppId}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CalendarDays className="w-4 h-4 text-gray-500" />
              <span className="text-gray-400">Dates:</span>
              <span className="font-medium">{campaign.startDate} — {campaign.endDate}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <DollarSign className="w-4 h-4 text-gray-500" />
              <span className="text-gray-400">Budget:</span>
              <span className="font-medium">${campaign.maxBudget?.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <DollarSign className="w-4 h-4 text-gray-500" />
              <span className="text-gray-400">
                Price/{campaign.campaignType}:
              </span>
              <span className="font-medium">
                ${campaign.campaignType === "purchase" ? campaign.pricePerPurchase : campaign.pricePerWishlist}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-gray-500" />
              <span className="text-gray-400">Max Influencers:</span>
              <span className="font-medium">{campaign.maxInfluencers || "Unlimited"}</span>
            </div>
            {campaign.minAudienceSize > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <Eye className="w-4 h-4 text-gray-500" />
                <span className="text-gray-400">Min Audience:</span>
                <span className="font-medium">{campaign.minAudienceSize?.toLocaleString()}+</span>
              </div>
            )}
          </div>

          {/* Steam Page */}
          {campaign.steamPageUrl && (
            <div className="mb-4">
              <a
                href={campaign.steamPageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300 transition-colors"
              >
                <LinkIcon className="w-4 h-4" />
                Steam Store Page
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}

          {/* Description */}
          <div className="mb-4">
            <p className="text-xs text-gray-500 mb-1">Description</p>
            <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
              {campaign.description}
            </p>
          </div>

          {/* Platforms */}
          {campaign.platforms && campaign.platforms.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">Platforms</p>
              <div className="flex flex-wrap gap-1.5">
                {campaign.platforms.map((p) => (
                  <span
                    key={p}
                    className="text-xs px-2.5 py-1 rounded-full bg-gray-800 text-gray-400 capitalize"
                  >
                    {p === "twitter" ? "X / Twitter" : p}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Genres */}
          {campaign.genres && campaign.genres.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">Genres</p>
              <div className="flex flex-wrap gap-1.5">
                {campaign.genres.map((g) => (
                  <span
                    key={g}
                    className="text-xs px-2.5 py-1 rounded-full bg-brand-500/10 text-brand-400 border border-brand-500/20"
                  >
                    {g}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Country Pricing */}
          {campaign.useCountryPricing && campaign.pricingTiers && campaign.pricingTiers.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Country-Based Pricing</p>
              <div className="space-y-2">
                {campaign.pricingTiers.map((tier, i) => (
                  <div key={i} className="bg-gray-800/50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium">{tier.name}</span>
                      <span className="text-sm font-semibold text-green-400">
                        ${tier.price} / {campaign.campaignType}
                      </span>
                    </div>
                    {tier.countries && tier.countries.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {tier.countries.map((c) => (
                          <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-400">
                            {c}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        Default tier — all other countries
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Media */}
        {(campaign.bannerImageUrl || campaign.galleryImageUrls?.length > 0 || campaign.youtubeTrailerUrl || campaign.mediaKitUrl) && (
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5 mb-6">
            <h3 className="text-sm font-medium text-gray-300 mb-4">Media</h3>

            {campaign.bannerImageUrl && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">Banner</p>
                <img
                  src={campaign.bannerImageUrl}
                  alt="Banner"
                  className="w-full max-h-48 object-cover rounded-xl border border-gray-800"
                />
              </div>
            )}

            {campaign.galleryImageUrls && campaign.galleryImageUrls.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">Gallery ({campaign.galleryImageUrls.length} images)</p>
                <div className="flex flex-wrap gap-2">
                  {campaign.galleryImageUrls.map((url, i) => (
                    <img
                      key={i}
                      src={url}
                      alt={`Gallery ${i + 1}`}
                      className="w-24 h-24 object-cover rounded-xl border border-gray-800"
                    />
                  ))}
                </div>
              </div>
            )}

            {campaign.youtubeTrailerUrl && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">YouTube Trailer</p>
                <a
                  href={campaign.youtubeTrailerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300"
                >
                  <MonitorPlay className="w-4 h-4" />
                  {campaign.youtubeTrailerUrl}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}

            {campaign.mediaKitUrl && (
              <div>
                <p className="text-xs text-gray-500 mb-2">Media Kit</p>
                <a
                  href={campaign.mediaKitUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300"
                >
                  <FolderOpen className="w-4 h-4" />
                  {campaign.mediaKitUrl}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        )}

        {/* Joined Influencers */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5">
          <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Joined Influencers ({joinedInfluencers.length})
          </h3>

          {joinedInfluencers.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No influencers have joined this campaign yet.
            </p>
          ) : (
            <div className="space-y-3">
              {joinedInfluencers.map((inf) => (
                <div
                  key={inf.id}
                  onClick={() => navigate(`/admin/influencers/${inf.influencerId}`)}
                  className="bg-gray-800/50 rounded-xl p-4 flex items-center justify-between hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400 font-bold text-xs">
                      {(inf.profile?.displayName || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {inf.profile?.displayName || "Unknown Influencer"}
                      </p>
                      <p className="text-xs text-gray-500">{inf.profile?.email || ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {inf.utmId && (
                      <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded-full">
                        UTM: {inf.utmId}
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/influencers/${inf.influencerId}`);
                      }}
                      className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-1.5 rounded-xl transition-all text-xs font-medium"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminCampaignReviewPage;
